import { t } from "@lingui/core/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import { useAtomValue, useSetAtom } from "jotai";

import { SlotLockCountdownTimer } from "@/features/LockSlots/components/SlotLockCountdownTimer/SlotLockCountdownTimer";
import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator/ClubStackNavigator";
import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import { Button } from "@whoosh/design-system/src/components/controls";
import { VStack, HStack } from "@whoosh/design-system/src/components/primitive";

import { goToNextDiningStepAtom } from "../../atoms/actions/goToNextStep/goToNextStep";
import { goToPreviousDiningStepAtom } from "../../atoms/actions/goToPreviousStep/goToPreviousStep";
import { isFinalDiningStepAtom } from "../../atoms/selectors/isFinalStep/isFinalStep";
import { isFirstDiningStepAtom } from "../../atoms/selectors/isFirstStep/isFirstStep";
import { DiningBookingWizardDataAtom } from "../../atoms/stores";
import { useCreateDraftDiningBooking } from "../../hooks/useCreateDraftDiningBooking/useCreateDraftDiningBooking";

type Props = {
  onClose: () => void;
  expiresAt: string | null;
};

export const DiningWizardFooter = ({ onClose, expiresAt }: Props) => {
  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();
  const isFirstStep = useAtomValue(isFirstDiningStepAtom);
  const isFinalStep = useAtomValue(isFinalDiningStepAtom);
  const goToPreviousStep = useSetAtom(goToPreviousDiningStepAtom);
  const goToNextStep = useSetAtom(goToNextDiningStepAtom);
  const isDisabled = false;
  const continueText = t`Continue`;

  const [handleCreateDraftDiningBooking, isCreatingDraftDiningBooking] =
    useCreateDraftDiningBooking();

  const diningBookingWizardData = useAtomValue(DiningBookingWizardDataAtom);
  const trackEvent = useTrackEvent();

  const handleWizardContinueOrComplete = () => {
    if (isFinalStep) {
      handleCreateDraftDiningBooking(
        {
          dietaryTags: diningBookingWizardData?.dietaryTags ?? [],
          occasionTags: diningBookingWizardData?.occasionTags ?? [],
          notes: diningBookingWizardData?.notes ?? "",
          otherPatronIds: diningBookingWizardData?.otherPatronIds ?? [],
          ownerId: diningBookingWizardData?.ownerId ?? "",
          reservationHoldId: diningBookingWizardData?.reservationHoldId ?? "",
        },
        (bookingId) => {
          trackEvent("BookingWizard.CreateDraftBooking", {
            bookingWizardData: diningBookingWizardData ?? null,
          });
          onClose();
          navigation.navigate("BookingSummary", {
            slotOrBookingId: bookingId,
            params: {},
          });
        }
      );
    } else {
      trackEvent("BookingWizard.PressContinue", {
        nextStep: "DiningAddKnownPeopleStep",
        bookingWizardData: diningBookingWizardData ?? null,
      });
      goToNextStep();
    }
  };

  return (
    <VStack padding={5} borderTopWidth={1.5} borderTopColor="outline-default">
      <HStack justifyContent="center">
        <SlotLockCountdownTimer
          lockedUntil={expiresAt}
          onExpireCallback={onClose}
        />
      </HStack>
      <HStack paddingTop={5} gap={4}>
        {!isFirstStep ? (
          <Button
            width={120}
            variant="secondary"
            accessibilityLabel={t`Back`}
            testID="booking-wizard-back-button"
            onPress={goToPreviousStep}
          >{t`Back`}</Button>
        ) : null}
        <Button
          style={{ flex: 1 }}
          accessibilityLabel={t`Booking Wizard Continue Button`}
          onPress={handleWizardContinueOrComplete}
          testID="booking-wizard-continue-button"
          isDisabled={isDisabled}
          isLoading={isCreatingDraftDiningBooking}
        >
          {continueText}
        </Button>
      </HStack>
    </VStack>
  );
};
